import { useState } from "react";
import { getProductFacePriceUsage } from "../../../services";
import { ProductFacePriceUsage } from "../../../types/product.type";
import { isFacePriceChanged } from "./productForm";

/** What `FacePriceImpactDialog` needs besides the save itself. */
export interface FacePriceImpactDialogState {
  /** Null while closed. */
  usage: ProductFacePriceUsage | null;
  selectedCodes: string[];
  onToggle: (pricebookCode: string) => void;
  onToggleAll: (checked: boolean) => void;
  onClose: () => void;
}

/**
 * The question a new face price raises before the product is saved: which pricebooks priced at
 * the old face price should move to the new one. Every such book starts ticked — its line was
 * set from the face price — and the user unticks the ones that keep their price.
 */
export function useFacePriceImpact() {
  const [usage, setUsage] = useState<ProductFacePriceUsage | null>(null);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [checking, setChecking] = useState<boolean>(false);

  /**
   * Resolves true when the save has to wait: the dialog opened for the user's choice, or the
   * usage read failed (the interceptor reports it) so nothing is saved without the question.
   * False when the face price is unchanged or no pricebook sits at the old one.
   */
  const holdSaveForChoice = async (
    productUuid: string,
    storedFacePrice: number | null,
    facePriceText: string,
  ): Promise<boolean> => {
    // Without a stored face price no pricebook line can sit at it.
    if (
      storedFacePrice === null ||
      !isFacePriceChanged(facePriceText, storedFacePrice)
    ) {
      return false;
    }
    setChecking(true);
    try {
      const {
        data: { data },
      } = await getProductFacePriceUsage(productUuid, Number(facePriceText));
      if (!data || data.pricebooks.length === 0) return false;
      setSelectedCodes(data.pricebooks.map((book) => book.pricebook_code));
      setUsage(data);
      return true;
    } catch {
      // Reported by the axios interceptor; holding the save is the whole response.
      return true;
    } finally {
      setChecking(false);
    }
  };

  const onToggle = (pricebookCode: string) => {
    setSelectedCodes((current) =>
      current.includes(pricebookCode)
        ? current.filter((code) => code !== pricebookCode)
        : [...current, pricebookCode],
    );
  };

  const onToggleAll = (checked: boolean) => {
    setSelectedCodes(
      checked && usage
        ? usage.pricebooks.map((book) => book.pricebook_code)
        : [],
    );
  };

  const onClose = () => setUsage(null);

  const dialogState: FacePriceImpactDialogState = {
    usage,
    selectedCodes,
    onToggle,
    onToggleAll,
    onClose,
  };

  return { checking, holdSaveForChoice, dialogState };
}
